import { FC, useCallback, useMemo, useState } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { Button, Upload } from '@hh.ru/magritte-ui';
import type { LoadedFile } from '@hh.ru/magritte-ui';
import type { Accept } from '@hh.ru/magritte-ui-upload/node_modules/react-dropzone';

import useTrls from 'hooks/translation/useTrls';

import { uploadOrdReport } from 'components/Admin/YandexDirectReport/OrdReport/OrdReportUploadField/uploadOrdReport';

import { addMagritteNotification } from 'store/models/notification';
import { useDispatch } from 'store/utils';

import styles from 'components/Admin/YandexDirectReport/OrdReport/OrdReportUploadField/OrdReportUploadField.module.less';

const ACCEPT_EXCEL_EXTENSIONS: Accept = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const TrlKeys = {
    button: 'admin.directReport.upload.button',
    select: 'admin.directReport.upload.select',
    description: 'admin.directReport.upload.description',
    renderFileInfo: {
        loading: 'admin.directReport.upload.renderFileInfo.loading',
        loaded: 'admin.directReport.upload.renderFileInfo.loaded',
        error: 'admin.directReport.upload.renderFileInfo.error',
    },
};

interface OrdReportUploadFieldProps {
    setIsUploadedOn: () => void;
    setIsUploadedOff: () => void;
}

export const OrdReportUploadField: FC<OrdReportUploadFieldProps> = ({ setIsUploadedOn, setIsUploadedOff }) => {
    const [uploadedFiles, setUploadedFiles] = useState<LoadedFile[]>([]);

    const dispatch = useDispatch();
    const trl = useTrls();

    const handleFileUpload = useCallback(
        async (file: File) => {
            try {
                await uploadOrdReport({
                    file,
                    onProgress: (percentage, totalMB) => {
                        setUploadedFiles((prevFiles) => {
                            if (!prevFiles.length) {
                                return prevFiles;
                            }
                            return prevFiles.map((file) => ({
                                ...file,
                                loadPercentage: percentage,
                                fileSizeMB: totalMB.toFixed(2),
                            }));
                        });
                    },
                });
                setUploadedFiles((prevFiles) => {
                    if (!prevFiles.length) {
                        return prevFiles;
                    }
                    return prevFiles.map((file) => ({
                        ...file,
                        loading: false,
                    }));
                });
                setIsUploadedOn();
            } catch (error) {
                setUploadedFiles((prevFiles) => {
                    if (!prevFiles.length) {
                        return prevFiles;
                    }
                    return prevFiles.map((file) => ({
                        ...file,
                        loading: false,
                        error: trl(TrlKeys.renderFileInfo.error),
                    }));
                });
            }
        },
        [setIsUploadedOn, trl]
    );

    const magritteTrls = useMemo(
        () => ({
            title: trl(TrlKeys.select),
            subTitle: trl(TrlKeys.description, [MAX_FILE_SIZE_MB]),
        }),
        [trl]
    );

    const handleMagritteDropAccepted = useCallback(
        (files: LoadedFile[]) => {
            setIsUploadedOff();
            const file = files[0]?.data;
            if (!file) {
                return;
            }

            setUploadedFiles([{ ...files[0], loading: true }]);
            void handleFileUpload(file);
        },
        [handleFileUpload, setIsUploadedOff]
    );

    const handleMagritteDropRejected = useCallback(() => {
        dispatch(addMagritteNotification(MagritteNotificationName.CampaignsReportError));
    }, [dispatch]);

    const handleMagritteFileDelete = useCallback(() => {
        setUploadedFiles([]);
        setIsUploadedOff();
    }, [setUploadedFiles, setIsUploadedOff]);

    return (
        <div className={styles.wrapper}>
            <Upload
                size="small"
                files={uploadedFiles}
                trls={magritteTrls}
                accept={ACCEPT_EXCEL_EXTENSIONS}
                maxSize={MAX_FILE_SIZE}
                selectFileButton={
                    <Button mode="secondary" size="small" style="accent">
                        {trl(TrlKeys.button)}
                    </Button>
                }
                renderFileInfo={(loadedFile) => {
                    const { loading, loadPercentage, fileSizeMB, error } = loadedFile;
                    if (error) {
                        return error;
                    }
                    if (loading) {
                        return trl(TrlKeys.renderFileInfo.loading, [
                            ((loadPercentage ?? 0) / 100) * Number(fileSizeMB),
                            fileSizeMB,
                        ]);
                    }
                    return trl(TrlKeys.renderFileInfo.loaded, [fileSizeMB]);
                }}
                onDropAccepted={handleMagritteDropAccepted}
                onDropRejected={handleMagritteDropRejected}
                onFileDelete={handleMagritteFileDelete}
            />
        </div>
    );
};
